import {
	BgColorsOutlined,
	BookOutlined,
	CopyOutlined,
	FontSizeOutlined,
	LayoutOutlined,
	PictureOutlined,
	SoundOutlined,
} from "@ant-design/icons";
import { Logo } from "@bodhistreams/ui";
import {
	Card,
	Col,
	Divider,
	Menu,
	message,
	Row,
	Space,
	Tag,
	Tooltip,
	Typography,
} from "antd";
import { useRef, useState } from "react";
import PageLayout from "./PageLayout";

const { Title, Text, Paragraph } = Typography;

// ─── Hardcoded Brand Tokens ──────────────────────────────────────────────────

const BRAND = {
	name: "BodhiStreams",
	tagline: "Tranquility, Harmony, Balance",
	description:
		"A mindful music streaming platform delivering original, high-quality 432 Hz wellness compositions for meditation, relaxation, and spiritual practice.",
	mission:
		"BodhiStreams exists to deliver original wellness music compositions that open and deepen the senses — deeply influenced by slow, meditative movements, ancient traditions, and relaxation techniques.",
	vision:
		"To be the definitive streaming platform for mindful, meditative, and devotional music — bridging the gap between the busy modern world and a space of tranquility, harmony, and balance.",
	values: [
		{
			title: "Mindfulness",
			desc: "Every design and sound decision honours calm awareness.",
		},
		{
			title: "Quality",
			desc: "All compositions are produced and mastered at 432 Hz in the highest fidelity.",
		},
		{
			title: "Accessibility",
			desc: "Wellness music should be available to everyone, everywhere.",
		},
		{
			title: "Authenticity",
			desc: "Traditional Asian instruments and ancient techniques guide the creative process.",
		},
	],
	story:
		"BodhiStreams original wellness compositions open and deepen the senses, deeply influenced by soft, slow-moving meditative movements, ancient traditions, and relaxation techniques. A textual and ethnic presence created by the use of traditional Asian instruments blends these wonderful pieces together, creating a tranquil space for meditation, relaxation, and devotional practices — helping the listener connect and reach higher levels of consciousness and enlightenment.",
	personality: [
		"Calm, grounded, warm",
		"Intentional, thoughtful",
		"Natural, organic",
		"Refined but approachable",
	],
};

const FONT_FAMILY =
	'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';

const BRAND_COLORS = [
	{
		name: "Primary — Sage Green",
		token: "colorPrimary",
		hex: "#6B9E8D",
		usage: "Primary actions, links, logo text, slider tracks",
	},
	{
		name: "Success — Mint",
		token: "colorSuccess",
		hex: "#7EBBA3",
		usage: "Success states, hover accents, pastel highlights",
	},
	{
		name: "Warning — Warm Sand",
		token: "colorWarning",
		hex: "#C9A876",
		usage: "Warning states, badges, warm accents",
	},
	{
		name: "Error — Terracotta",
		token: "colorError",
		hex: "#D4847A",
		usage: "Error states, destructive actions",
	},
	{
		name: "Info — Sage Green",
		token: "colorInfo",
		hex: "#6B9E8D",
		usage: "Informational highlights",
	},
];

const LIGHT_COLORS = [
	{
		name: "Background Base",
		token: "colorBgBase",
		hex: "#F8F7F5",
		friendlyName: "Warm White",
	},
	{
		name: "Background Container",
		token: "colorBgContainer",
		hex: "#F8F7F5",
		friendlyName: "Warm White",
	},
	{
		name: "Background Elevated",
		token: "colorBgElevated",
		hex: "#F2EFE9",
		friendlyName: "Parchment",
	},
	{
		name: "Background Layout",
		token: "colorBgLayout",
		hex: "#EDE8E0",
		friendlyName: "Linen",
	},
	{
		name: "Border Primary",
		token: "colorBorder",
		hex: "#D4CCC2",
		friendlyName: "Sand",
	},
	{
		name: "Border Secondary",
		token: "colorBorderSecondary",
		hex: "#E5DFD7",
		friendlyName: "Light Sand",
	},
	{
		name: "Text Primary",
		token: "colorText",
		hex: "rgba(40, 50, 45, 0.92)",
		friendlyName: "Charcoal",
	},
	{
		name: "Text Secondary",
		token: "colorTextSecondary",
		hex: "rgba(80, 95, 90, 0.72)",
		friendlyName: "Muted Sage",
	},
	{
		name: "Text Tertiary",
		token: "colorTextTertiary",
		hex: "rgba(100, 115, 110, 0.58)",
		friendlyName: "Faded Sage",
	},
	{
		name: "Text Quaternary",
		token: "colorTextQuaternary",
		hex: "rgba(120, 130, 125, 0.40)",
		friendlyName: "Ghost",
	},
];

const DARK_COLORS = [
	{
		name: "Background Base",
		token: "colorBgBase",
		hex: "#1A1F1D",
		friendlyName: "Deep Forest",
	},
	{
		name: "Background Container",
		token: "colorBgContainer",
		hex: "#212825",
		friendlyName: "Dark Moss",
	},
	{
		name: "Background Elevated",
		token: "colorBgElevated",
		hex: "#2B3330",
		friendlyName: "Forest Floor",
	},
	{
		name: "Background Layout",
		token: "colorBgLayout",
		hex: "#1A1F1D",
		friendlyName: "Deep Forest",
	},
	{
		name: "Border Primary",
		token: "colorBorder",
		hex: "#3A4542",
		friendlyName: "Shadow Pine",
	},
	{
		name: "Border Secondary",
		token: "colorBorderSecondary",
		hex: "#2F3834",
		friendlyName: "Dark Pine",
	},
	{
		name: "Text Primary",
		token: "colorText",
		hex: "#E8E5E0",
		friendlyName: "Warm Cream",
	},
	{
		name: "Text Secondary",
		token: "colorTextSecondary",
		hex: "#B8B5B0",
		friendlyName: "Muted Cream",
	},
	{
		name: "Text Tertiary",
		token: "colorTextTertiary",
		hex: "#8F8B86",
		friendlyName: "Stone",
	},
	{
		name: "Text Quaternary",
		token: "colorTextQuaternary",
		hex: "#626259",
		friendlyName: "Dim Stone",
	},
];

const ACCENT_COLORS = [
	{ name: "Pink", hex: "#C9A5A0", rgba: "rgba(201, 165, 160, 0.4)" },
	{ name: "Purple", hex: "#8B7B9D", rgba: "rgba(139, 123, 157, 0.4)" },
	{ name: "Blue (Mint)", hex: "#7EBBA3", rgba: "rgba(126, 187, 163, 0.4)" },
	{ name: "Green", hex: "#6B9E8D", rgba: "rgba(107, 158, 141, 0.4)" },
	{ name: "Orange", hex: "#C9A876", rgba: "rgba(201, 168, 118, 0.4)" },
	{ name: "Red", hex: "#D4847A", rgba: "rgba(212, 132, 122, 0.4)" },
];

const COMPONENT_COLORS_LIGHT = [
	{ component: "Card", property: "Background", value: "#F2EFE9" },
	{
		component: "Card",
		property: "Shadow",
		value: "0 2px 6px rgba(107,158,141,0.08)",
	},
	{ component: "Button", property: "Hover", value: "#7EBBA3" },
	{ component: "Button", property: "Active", value: "#5A8A76" },
	{ component: "Input", property: "Background", value: "#F8F7F5" },
	{ component: "Layout Header", property: "Background", value: "#F8F7F5" },
	{ component: "Layout Body", property: "Background", value: "#F8F7F5" },
	{ component: "Layout Sidebar", property: "Background", value: "#F2EFE9" },
	{
		component: "Menu Item",
		property: "Hover BG",
		value: "rgba(107, 158, 141, 0.06)",
	},
	{
		component: "Menu Item",
		property: "Selected BG",
		value: "rgba(107, 158, 141, 0.12)",
	},
	{
		component: "Menu Item",
		property: "Color",
		value: "rgba(80, 95, 90, 0.72)",
	},
	{ component: "Menu Item", property: "Selected Color", value: "#5A8A76" },
	{ component: "Slider", property: "Rail", value: "#D4CCC2" },
	{ component: "Slider", property: "Track", value: "#6B9E8D" },
	{ component: "Badge", property: "Background", value: "#D4847A" },
	{ component: "Avatar", property: "Background", value: "#EDE8E0" },
];

const COMPONENT_COLORS_DARK = [
	{
		component: "Card",
		property: "Background",
		value: "rgba(43, 51, 48, 0.95)",
	},
	{
		component: "Card",
		property: "Shadow",
		value: "0 2px 6px rgba(107,158,141,0.12)",
	},
	{ component: "Input", property: "Background", value: "#212825" },
	{ component: "Input", property: "Border", value: "#3A4542" },
	{ component: "Layout Header", property: "Background", value: "#212825" },
	{ component: "Layout Body", property: "Background", value: "#1A1F1D" },
	{ component: "Layout Sidebar", property: "Background", value: "#1F2623" },
	{
		component: "Menu Item",
		property: "Hover BG",
		value: "rgba(126, 187, 163, 0.08)",
	},
	{
		component: "Menu Item",
		property: "Selected BG",
		value: "rgba(107, 158, 141, 0.18)",
	},
	{ component: "Menu Item", property: "Color", value: "#8F8B86" },
	{ component: "Menu Item", property: "Selected Color", value: "#7EBBA3" },
	{ component: "Slider", property: "Rail", value: "#2F3834" },
	{ component: "Slider", property: "Track", value: "#6B9E8D" },
	{ component: "Badge", property: "Background", value: "#D4847A" },
	{ component: "Avatar", property: "Background", value: "#2B3330" },
];

const SHADOWS_LIGHT = {
	primary: "0 2px 8px rgba(40, 50, 45, 0.06), 0 1px 3px rgba(40, 50, 45, 0.08)",
	secondary:
		"0 4px 12px rgba(40, 50, 45, 0.08), 0 2px 4px rgba(40, 50, 45, 0.10)",
};

const SHADOWS_DARK = {
	primary: "0 2px 8px rgba(0, 0, 0, 0.20), 0 1px 3px rgba(0, 0, 0, 0.15)",
	secondary: "0 4px 12px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.20)",
};

const TYPE_SCALE = [
	{
		level: "H1 (Title)",
		size: "38px",
		weight: "600",
		usage: "Hero sections, page titles",
	},
	{ level: "H2", size: "30px", weight: "600", usage: "Section headings" },
	{ level: "H3", size: "24px", weight: "600", usage: "Sub-section headings" },
	{ level: "H4", size: "20px", weight: "600", usage: "Card titles" },
	{ level: "H5", size: "16px", weight: "600", usage: "Minor headings" },
	{ level: "Body", size: "14px", weight: "400", usage: "Default body copy" },
	{
		level: "Secondary",
		size: "14px",
		weight: "400",
		usage: "Captions, metadata",
	},
	{ level: "Small", size: "12px", weight: "400", usage: "Labels, footnotes" },
];

// ─── Page Theme System ───────────────────────────────────────────────────────
// These control the BrandBook page appearance — NOT the brand tokens themselves.

const PAGE_THEMES = {
	dark: {
		bgBase: "#1A1F1D",
		bgContainer: "#212825",
		bgElevated: "#2B3330",
		border: "#3A4542",
		borderSecondary: "#2F3834",
		borderSubtle: "rgba(58, 69, 66, 0.5)",
		textPrimary: "#E8E5E0",
		textSecondary: "#B8B5B0",
		textTertiary: "#8F8B86",
		accent: "#7EBBA3",
		accentMuted: "#6B9E8D",
		accentBg: "rgba(107, 158, 141, 0.06)",
		accentBgHover: "rgba(107, 158, 141, 0.08)",
		accentBgActive: "rgba(107, 158, 141, 0.12)",
		accentBorder: "rgba(107, 158, 141, 0.15)",
		accentBorderStrong: "rgba(107, 158, 141, 0.25)",
		swatchBg: "#212825",
		swatchBorder: "rgba(107, 158, 141, 0.1)",
		heroGradient:
			"linear-gradient(135deg, #1A1F1D 0%, #212825 50%, #2B3330 100%)",
		contentGradient: "linear-gradient(180deg, #1A1F1D 0%, #212825 100%)",
		isDark: true,
	},
	light: {
		bgBase: "#F8F7F5",
		bgContainer: "#F2EFE9",
		bgElevated: "#EDE8E0",
		border: "#D4CCC2",
		borderSecondary: "#E5DFD7",
		borderSubtle: "rgba(212, 204, 194, 0.5)",
		textPrimary: "rgba(40, 50, 45, 0.92)",
		textSecondary: "rgba(80, 95, 90, 0.72)",
		textTertiary: "rgba(100, 115, 110, 0.58)",
		accent: "#5A8A76",
		accentMuted: "#6B9E8D",
		accentBg: "rgba(107, 158, 141, 0.06)",
		accentBgHover: "rgba(107, 158, 141, 0.08)",
		accentBgActive: "rgba(107, 158, 141, 0.12)",
		accentBorder: "rgba(107, 158, 141, 0.15)",
		accentBorderStrong: "rgba(107, 158, 141, 0.25)",
		swatchBg: "#F2EFE9",
		swatchBorder: "rgba(212, 204, 194, 0.3)",
		heroGradient:
			"linear-gradient(135deg, #F8F7F5 0%, #F2EFE9 50%, #EDE8E0 100%)",
		contentGradient: "linear-gradient(180deg, #F8F7F5 0%, #F2EFE9 100%)",
		isDark: false,
	},
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const copyToClipboard = (text) => {
	navigator.clipboard.writeText(text);
	message.success({ content: `Copied: ${text}`, duration: 1.5 });
};

const isRgba = (color) => color.startsWith("rgba");
const isBright = (color) => {
	if (isRgba(color)) {
		const match = color.match(
			/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/,
		);
		if (match) {
			const [, r, g, b, a] = match;
			const alpha = Number.parseFloat(a ?? "1");
			if (alpha < 0.5) return true;
			return Number(r) * 0.299 + Number(g) * 0.587 + Number(b) * 0.114 > 150;
		}
	}
	if (color.startsWith("#")) {
		const hex = color.replace("#", "");
		const r = Number.parseInt(hex.substring(0, 2), 16);
		const g = Number.parseInt(hex.substring(2, 4), 16);
		const b = Number.parseInt(hex.substring(4, 6), 16);
		return r * 0.299 + g * 0.587 + b * 0.114 > 150;
	}
	return false;
};

// ─── Sub-Components ──────────────────────────────────────────────────────────

const ColorSwatch = ({
	name,
	hex,
	friendlyName,
	token,
	usage,
	size = "normal",
	t,
}) => {
	const bright = isBright(hex);
	const textColor = bright ? "#1A1F1D" : "#F8F7F5";
	const isSmall = size === "small";

	return (
		<Tooltip title="Click swatch to copy HEX">
			<div
				role="button"
				tabIndex={0}
				onClick={() => copyToClipboard(hex)}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") copyToClipboard(hex);
				}}
				style={{
					borderRadius: 10,
					overflow: "hidden",
					cursor: "pointer",
					border: `1px solid ${t.accentBorder}`,
					transition: "transform 0.2s, box-shadow 0.2s",
				}}
				onMouseEnter={(e) => {
					e.currentTarget.style.transform = "translateY(-2px)";
					e.currentTarget.style.boxShadow =
						"0 6px 16px rgba(107, 158, 141, 0.15)";
				}}
				onMouseLeave={(e) => {
					e.currentTarget.style.transform = "translateY(0)";
					e.currentTarget.style.boxShadow = "none";
				}}
			>
				<div
					style={{
						height: isSmall ? 56 : 80,
						backgroundColor: hex,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						position: "relative",
					}}
				>
					<CopyOutlined
						style={{
							fontSize: isSmall ? 14 : 18,
							color: textColor,
							opacity: 0.6,
						}}
					/>
				</div>
				<div
					style={{
						padding: isSmall ? "6px 8px" : "10px 12px",
						background: t.swatchBg,
						borderTop: `1px solid ${t.swatchBorder}`,
					}}
				>
					<Text
						strong
						style={{
							display: "block",
							fontSize: isSmall ? 11 : 13,
							color: t.textPrimary,
							lineHeight: 1.3,
						}}
					>
						{friendlyName || name}
					</Text>
					<Text
						style={{
							fontSize: isSmall ? 10 : 11,
							color: t.textTertiary,
							fontFamily: "monospace",
							display: "block",
							lineHeight: 1.4,
						}}
					>
						{hex}
					</Text>
					{token && !isSmall && (
						<Tag
							style={{
								marginTop: 4,
								fontSize: 10,
								background: t.accentBgActive,
								border: "none",
								color: t.accent,
								cursor: "pointer",
							}}
							onClick={(e) => {
								e.stopPropagation();
								copyToClipboard(token);
							}}
						>
							{token}
						</Tag>
					)}
					{usage && !isSmall && (
						<Text
							style={{
								fontSize: 10,
								color: t.textTertiary,
								display: "block",
								marginTop: 4,
								lineHeight: 1.3,
							}}
						>
							{usage}
						</Text>
					)}
				</div>
			</div>
		</Tooltip>
	);
};

const SectionTitle = ({ icon, title, subtitle, t }) => (
	<div style={{ marginBottom: 24 }}>
		<Title
			level={3}
			style={{
				marginBottom: 4,
				display: "flex",
				alignItems: "center",
				gap: 10,
				color: t.textPrimary,
			}}
		>
			{icon}
			{title}
		</Title>
		{subtitle && (
			<Text style={{ color: t.textTertiary, fontSize: 14 }}>{subtitle}</Text>
		)}
	</div>
);

const ComponentTable = ({ data, t }) => (
	<div style={{ overflowX: "auto" }}>
		<table
			style={{
				width: "100%",
				borderCollapse: "separate",
				borderSpacing: 0,
				fontSize: 13,
			}}
		>
			<thead>
				<tr>
					{["Component", "Property", "Value", "Preview"].map((h) => (
						<th
							key={h}
							style={{
								padding: "10px 14px",
								textAlign: "left",
								background: t.accentBgHover,
								color: t.accent,
								fontWeight: 600,
								fontSize: 11,
								textTransform: "uppercase",
								letterSpacing: 0.5,
								borderBottom: `1px solid ${t.border}`,
							}}
						>
							{h}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{data.map((row) => (
					<tr key={`${row.component}-${row.property}`}>
						<td
							style={{
								padding: "8px 14px",
								color: t.textPrimary,
								fontWeight: 500,
								borderBottom: `1px solid ${t.borderSubtle}`,
							}}
						>
							{row.component}
						</td>
						<td
							style={{
								padding: "8px 14px",
								color: t.textSecondary,
								borderBottom: `1px solid ${t.borderSubtle}`,
							}}
						>
							{row.property}
						</td>
						<td
							style={{
								padding: "8px 14px",
								fontFamily: "monospace",
								fontSize: 11,
								color: t.accent,
								cursor: "pointer",
								borderBottom: `1px solid ${t.borderSubtle}`,
							}}
							onClick={() => copyToClipboard(row.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ")
									copyToClipboard(row.value);
							}}
						>
							{row.value}
						</td>
						<td
							style={{
								padding: "8px 14px",
								borderBottom: `1px solid ${t.borderSubtle}`,
							}}
						>
							{!row.value.includes("0 ") && (
								<div
									style={{
										width: 28,
										height: 28,
										borderRadius: 6,
										backgroundColor: row.value,
										border: `1px solid ${t.accentBorder}`,
									}}
								/>
							)}
						</td>
					</tr>
				))}
			</tbody>
		</table>
	</div>
);

// ─── Sections ────────────────────────────────────────────────────────────────

const BrandFoundation = ({ t }) => (
	<div>
		<SectionTitle
			icon={<BookOutlined />}
			title="Brand Foundation"
			subtitle="Who we are and what we stand for"
			t={t}
		/>

		<Row gutter={[24, 24]}>
			<Col xs={24} lg={12}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
						height: "100%",
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 4 }}>
						Mission
					</Title>
					<Paragraph
						style={{ color: t.textSecondary, marginBottom: 24, fontSize: 14 }}
					>
						{BRAND.mission}
					</Paragraph>

					<Title level={5} style={{ color: t.accent, marginBottom: 4 }}>
						Vision
					</Title>
					<Paragraph
						style={{ color: t.textSecondary, marginBottom: 0, fontSize: 14 }}
					>
						{BRAND.vision}
					</Paragraph>
				</Card>
			</Col>

			<Col xs={24} lg={12}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
						height: "100%",
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 12 }}>
						Core Values
					</Title>
					<Space orientation="vertical" size={12} style={{ width: "100%" }}>
						{BRAND.values.map((v) => (
							<div key={v.title}>
								<Text strong style={{ color: t.textPrimary }}>
									{v.title}
								</Text>
								<br />
								<Text style={{ color: t.textTertiary, fontSize: 13 }}>
									{v.desc}
								</Text>
							</div>
						))}
					</Space>
				</Card>
			</Col>

			<Col xs={24}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 4 }}>
						Brand Story
					</Title>
					<Paragraph
						style={{ color: t.textSecondary, fontSize: 14, marginBottom: 0 }}
					>
						{BRAND.story}
					</Paragraph>
				</Card>
			</Col>
		</Row>
	</div>
);

const PersonalityVoice = ({ t }) => (
	<div>
		<SectionTitle
			icon={<SoundOutlined />}
			title="Personality & Voice"
			subtitle="How we communicate"
			t={t}
		/>

		<Row gutter={[24, 24]}>
			<Col xs={24} md={12}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
						height: "100%",
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 12 }}>
						Personality Traits
					</Title>
					<Space orientation="vertical" size={8}>
						{BRAND.personality.map((trait) => (
							<Tag
								key={trait}
								style={{
									background: t.accentBgActive,
									border: `1px solid ${t.accentBorderStrong}`,
									color: t.accent,
									fontSize: 13,
									padding: "4px 12px",
									borderRadius: 20,
								}}
							>
								{trait}
							</Tag>
						))}
					</Space>
				</Card>
			</Col>

			<Col xs={24} md={12}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
						height: "100%",
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 12 }}>
						Tone Guidelines
					</Title>
					<Space orientation="vertical" size={12} style={{ width: "100%" }}>
						{[
							["Serene, not clinical", "Write as if speaking in a quiet room."],
							["Inviting, not salesy", "Guide the listener, don't push."],
							[
								"Simple, not simplistic",
								"Clear language with depth of meaning.",
							],
						].map(([title, desc]) => (
							<div key={title}>
								<Text strong style={{ color: t.textPrimary }}>
									{title}
								</Text>
								<br />
								<Text style={{ color: t.textTertiary, fontSize: 13 }}>
									{desc}
								</Text>
							</div>
						))}
					</Space>
				</Card>
			</Col>

			<Col xs={24}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 12 }}>
						Do / Don't
					</Title>
					<Row gutter={24}>
						<Col xs={24} md={12}>
							<div style={{ marginBottom: 8 }}>
								<Tag color="success">DO</Tag>
							</div>
							{[
								"Discover tranquil soundscapes",
								"A mindful space for your practice",
								"Composed for inner harmony",
							].map((txt) => (
								<Paragraph
									key={txt}
									style={{
										color: t.textSecondary,
										marginBottom: 6,
										fontSize: 13,
										paddingLeft: 8,
									}}
								>
									"{txt}"
								</Paragraph>
							))}
						</Col>
						<Col xs={24} md={12}>
							<div style={{ marginBottom: 8 }}>
								<Tag color="error">DON'T</Tag>
							</div>
							{[
								"Listen to our BEST tracks NOW!",
								"The world's greatest meditation app",
								"Super relaxing beats for chilling",
							].map((txt) => (
								<Paragraph
									key={txt}
									style={{
										color: t.textTertiary,
										marginBottom: 6,
										fontSize: 13,
										paddingLeft: 8,
										textDecoration: "line-through",
									}}
								>
									"{txt}"
								</Paragraph>
							))}
						</Col>
					</Row>
				</Card>
			</Col>
		</Row>
	</div>
);

const LogoSection = ({ t }) => (
	<div>
		<SectionTitle
			icon={<PictureOutlined />}
			title="Logo System"
			subtitle="Always written as BodhiStreams — one word, capital B and S"
			t={t}
		/>

		<Row gutter={[24, 24]}>
			{/* Light bg logos */}
			<Col xs={24} md={8}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Text
						style={{
							color: t.accent,
							fontSize: 11,
							textTransform: "uppercase",
							letterSpacing: 0.5,
							fontWeight: 600,
						}}
					>
						Light Background
					</Text>
					<div
						style={{
							marginTop: 12,
							padding: 32,
							background: "#F8F7F5",
							borderRadius: 10,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Logo.IconText
							hyperlink={false}
							size={36}
							imageUrl={Logo.logoBlack}
						/>
					</div>
				</Card>
			</Col>

			{/* Dark bg logos */}
			<Col xs={24} md={8}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Text
						style={{
							color: t.accent,
							fontSize: 11,
							textTransform: "uppercase",
							letterSpacing: 0.5,
							fontWeight: 600,
						}}
					>
						Dark Background
					</Text>
					<div
						style={{
							marginTop: 12,
							padding: 32,
							background: "#1A1F1D",
							borderRadius: 10,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Logo.IconText hyperlink={false} size={36} />
					</div>
				</Card>
			</Col>

			{/* Icon only */}
			<Col xs={24} md={8}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Text
						style={{
							color: t.accent,
							fontSize: 11,
							textTransform: "uppercase",
							letterSpacing: 0.5,
							fontWeight: 600,
						}}
					>
						Icon Only (Favicon)
					</Text>
					<div
						style={{
							marginTop: 12,
							display: "flex",
							gap: 16,
							justifyContent: "center",
						}}
					>
						<div
							style={{
								padding: 24,
								background: "#F8F7F5",
								borderRadius: 10,
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Logo.Favicon
								hyperlink={false}
								size={40}
								imageUrl={Logo.logoBlack}
							/>
						</div>
						<div
							style={{
								padding: 24,
								background: "#1A1F1D",
								borderRadius: 10,
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Logo.Favicon hyperlink={false} size={40} />
						</div>
					</div>
				</Card>
			</Col>

			{/* Logo variants table */}
			<Col xs={24}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 12 }}>
						Logo Variants
					</Title>
					<table
						style={{
							width: "100%",
							borderCollapse: "separate",
							borderSpacing: 0,
							fontSize: 13,
						}}
					>
						<thead>
							<tr>
								{["Variant", "File", "Usage"].map((h) => (
									<th
										key={h}
										style={{
											padding: "10px 14px",
											textAlign: "left",
											background: t.accentBgHover,
											color: t.accent,
											fontWeight: 600,
											fontSize: 11,
											textTransform: "uppercase",
											letterSpacing: 0.5,
											borderBottom: `1px solid ${t.border}`,
										}}
									>
										{h}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{[
								[
									"Green (Primary)",
									"logo_green.png",
									"Light backgrounds, default usage",
								],
								["White", "logo_white.png", "Dark backgrounds, overlays"],
								["Black", "logo_black.png", "Monochrome print, high-contrast"],
							].map(([variant, file, usage]) => (
								<tr key={variant}>
									<td
										style={{
											padding: "8px 14px",
											color: t.textPrimary,
											fontWeight: 500,
											borderBottom: `1px solid ${t.borderSubtle}`,
										}}
									>
										{variant}
									</td>
									<td
										style={{
											padding: "8px 14px",
											color: t.accent,
											fontFamily: "monospace",
											fontSize: 12,
											borderBottom: `1px solid ${t.borderSubtle}`,
										}}
									>
										{file}
									</td>
									<td
										style={{
											padding: "8px 14px",
											color: t.textSecondary,
											borderBottom: `1px solid ${t.borderSubtle}`,
										}}
									>
										{usage}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</Card>
			</Col>
		</Row>
	</div>
);

const BrandColorsSection = ({ t }) => (
	<div>
		<SectionTitle
			icon={<BgColorsOutlined />}
			title="Brand Colors"
			subtitle="Shared across light and dark themes — SPA-inspired: calm, cool, grounded"
			t={t}
		/>
		<Row gutter={[16, 16]}>
			{BRAND_COLORS.map((c) => (
				<Col xs={12} sm={8} md={6} lg={4} key={c.token}>
					<ColorSwatch {...c} t={t} />
				</Col>
			))}
		</Row>
	</div>
);

const LightThemeSection = ({ t }) => (
	<div>
		<SectionTitle
			icon={<BgColorsOutlined />}
			title="Light Theme Palette"
			subtitle="Soft, calming neutrals — warm whites, linens, and earthy tones"
			t={t}
		/>
		<Row gutter={[16, 16]}>
			{LIGHT_COLORS.map((c) => (
				<Col xs={12} sm={8} md={6} lg={4} key={c.token}>
					<ColorSwatch {...c} t={t} />
				</Col>
			))}
		</Row>

		<div style={{ marginTop: 32 }}>
			<Title level={5} style={{ color: t.accent, marginBottom: 16 }}>
				Component Overrides — Light
			</Title>
			<Card
				style={{
					background: t.bgContainer,
					border: `1px solid ${t.border}`,
					borderRadius: 12,
				}}
			>
				<ComponentTable data={COMPONENT_COLORS_LIGHT} t={t} />
			</Card>
		</div>
	</div>
);

const DarkThemeSection = ({ t }) => (
	<div>
		<SectionTitle
			icon={<BgColorsOutlined />}
			title="Dark Theme Palette"
			subtitle="Deep, calming neutrals — forest greens, mossy darks, and warm creams"
			t={t}
		/>
		<Row gutter={[16, 16]}>
			{DARK_COLORS.map((c) => (
				<Col xs={12} sm={8} md={6} lg={4} key={c.token}>
					<ColorSwatch {...c} t={t} />
				</Col>
			))}
		</Row>

		<div style={{ marginTop: 32 }}>
			<Title level={5} style={{ color: t.accent, marginBottom: 16 }}>
				Component Overrides — Dark
			</Title>
			<Card
				style={{
					background: t.bgContainer,
					border: `1px solid ${t.border}`,
					borderRadius: 12,
				}}
			>
				<ComponentTable data={COMPONENT_COLORS_DARK} t={t} />
			</Card>
		</div>
	</div>
);

const AccentColorsSection = ({ t }) => (
	<div>
		<SectionTitle
			icon={<BgColorsOutlined />}
			title="Accent / Separator Colors"
			subtitle="Used for section dividers, highlights, and decorative elements"
			t={t}
		/>
		<Row gutter={[16, 16]}>
			{ACCENT_COLORS.map((c) => (
				<Col xs={12} sm={8} md={6} lg={4} key={c.name}>
					<ColorSwatch
						name={c.name}
						hex={c.hex}
						friendlyName={c.name}
						usage={`RGBA: ${c.rgba}`}
						t={t}
					/>
				</Col>
			))}
		</Row>
	</div>
);

const TypographySection = ({ t }) => (
	<div>
		<SectionTitle
			icon={<FontSizeOutlined />}
			title="Typography"
			subtitle="System font stack for optimal rendering across all platforms"
			t={t}
		/>

		<Row gutter={[24, 24]}>
			<Col xs={24}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 8 }}>
						Font Stack
					</Title>
					<div
						role="button"
						tabIndex={0}
						style={{
							background: t.accentBg,
							padding: "12px 16px",
							borderRadius: 8,
							fontFamily: "monospace",
							fontSize: 12,
							color: t.textSecondary,
							cursor: "pointer",
							border: `1px solid ${t.accentBgActive}`,
						}}
						onClick={() => copyToClipboard(FONT_FAMILY)}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ")
								copyToClipboard(FONT_FAMILY);
						}}
					>
						{FONT_FAMILY}
					</div>

					<Row gutter={16} style={{ marginTop: 20 }}>
						<Col xs={8}>
							<Text
								style={{
									color: t.textTertiary,
									fontSize: 11,
									textTransform: "uppercase",
									letterSpacing: 0.5,
								}}
							>
								Base Size
							</Text>
							<br />
							<Text strong style={{ color: t.textPrimary, fontSize: 16 }}>
								14px
							</Text>
						</Col>
						<Col xs={8}>
							<Text
								style={{
									color: t.textTertiary,
									fontSize: 11,
									textTransform: "uppercase",
									letterSpacing: 0.5,
								}}
							>
								Border Radius
							</Text>
							<br />
							<Text strong style={{ color: t.textPrimary, fontSize: 16 }}>
								8px
							</Text>
						</Col>
						<Col xs={8}>
							<Text
								style={{
									color: t.textTertiary,
									fontSize: 11,
									textTransform: "uppercase",
									letterSpacing: 0.5,
								}}
							>
								Default Weight
							</Text>
							<br />
							<Text strong style={{ color: t.textPrimary, fontSize: 16 }}>
								400
							</Text>
						</Col>
					</Row>
				</Card>
			</Col>

			<Col xs={24}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 16 }}>
						Type Scale
					</Title>

					<table
						style={{
							width: "100%",
							borderCollapse: "separate",
							borderSpacing: 0,
							fontSize: 13,
						}}
					>
						<thead>
							<tr>
								{["Level", "Size", "Weight", "Usage"].map((h) => (
									<th
										key={h}
										style={{
											padding: "10px 14px",
											textAlign: "left",
											background: t.accentBgHover,
											color: t.accent,
											fontWeight: 600,
											fontSize: 11,
											textTransform: "uppercase",
											letterSpacing: 0.5,
											borderBottom: `1px solid ${t.border}`,
										}}
									>
										{h}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{TYPE_SCALE.map((row) => (
								<tr key={row.level}>
									<td
										style={{
											padding: "8px 14px",
											color: t.textPrimary,
											fontWeight: 500,
											borderBottom: `1px solid ${t.borderSubtle}`,
										}}
									>
										{row.level}
									</td>
									<td
										style={{
											padding: "8px 14px",
											fontFamily: "monospace",
											color: t.accent,
											borderBottom: `1px solid ${t.borderSubtle}`,
										}}
									>
										{row.size}
									</td>
									<td
										style={{
											padding: "8px 14px",
											color: t.textSecondary,
											borderBottom: `1px solid ${t.borderSubtle}`,
										}}
									>
										{row.weight}
									</td>
									<td
										style={{
											padding: "8px 14px",
											color: t.textTertiary,
											borderBottom: `1px solid ${t.borderSubtle}`,
										}}
									>
										{row.usage}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</Card>
			</Col>

			{/* Live typography preview */}
			<Col xs={24}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 16 }}>
						Live Preview
					</Title>
					<div
						style={{
							padding: 24,
							background: t.bgBase,
							borderRadius: 10,
						}}
					>
						<Title style={{ color: t.textPrimary }}>
							Heading 1 — Display Title
						</Title>
						<Title level={2} style={{ color: t.textPrimary }}>
							Heading 2 — Section Title
						</Title>
						<Title level={3} style={{ color: t.textPrimary }}>
							Heading 3 — Subsection
						</Title>
						<Title level={4} style={{ color: t.textPrimary }}>
							Heading 4 — Card Title
						</Title>
						<Title level={5} style={{ color: t.textPrimary }}>
							Heading 5 — Minor Heading
						</Title>
						<Paragraph style={{ color: t.textSecondary, fontSize: 14 }}>
							Body text — BodhiStreams is a mindful music streaming platform
							delivering original, high-quality 432 Hz wellness compositions for
							meditation, relaxation, and spiritual practice.
						</Paragraph>
						<Text style={{ color: t.textTertiary }}>Secondary text</Text>
						{" · "}
						<Text style={{ color: "#7EBBA3" }}>Success text</Text>
						{" · "}
						<Text style={{ color: "#C9A876" }}>Warning text</Text>
						{" · "}
						<Text style={{ color: "#D4847A" }}>Danger text</Text>
					</div>
				</Card>
			</Col>
		</Row>
	</div>
);

const ShadowsSection = ({ t }) => (
	<div>
		<SectionTitle
			icon={<LayoutOutlined />}
			title="Shadows & Spacing"
			subtitle="Soft, gentle shadows for depth hierarchy"
			t={t}
		/>

		<Row gutter={[24, 24]}>
			<Col xs={24} md={12}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 16 }}>
						Light Theme Shadows
					</Title>
					<Space orientation="vertical" size={16} style={{ width: "100%" }}>
						{Object.entries(SHADOWS_LIGHT).map(([name, value]) => (
							<div key={name}>
								<Text
									style={{
										color: t.textTertiary,
										fontSize: 11,
										textTransform: "uppercase",
										letterSpacing: 0.5,
									}}
								>
									{name}
								</Text>
								<div
									style={{
										marginTop: 8,
										padding: 20,
										background: "#F8F7F5",
										borderRadius: 10,
										boxShadow: value,
									}}
								>
									<Text
										style={{
											color: "rgba(40, 50, 45, 0.92)",
											fontSize: 12,
											fontFamily: "monospace",
										}}
									>
										{value}
									</Text>
								</div>
							</div>
						))}
					</Space>
				</Card>
			</Col>

			<Col xs={24} md={12}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 16 }}>
						Dark Theme Shadows
					</Title>
					<Space orientation="vertical" size={16} style={{ width: "100%" }}>
						{Object.entries(SHADOWS_DARK).map(([name, value]) => (
							<div key={name}>
								<Text
									style={{
										color: t.textTertiary,
										fontSize: 11,
										textTransform: "uppercase",
										letterSpacing: 0.5,
									}}
								>
									{name}
								</Text>
								<div
									style={{
										marginTop: 8,
										padding: 20,
										background: "#1A1F1D",
										borderRadius: 10,
										boxShadow: value,
									}}
								>
									<Text
										style={{
											color: "#E8E5E0",
											fontSize: 12,
											fontFamily: "monospace",
										}}
									>
										{value}
									</Text>
								</div>
							</div>
						))}
					</Space>
				</Card>
			</Col>

			<Col xs={24}>
				<Card
					style={{
						background: t.bgContainer,
						border: `1px solid ${t.border}`,
						borderRadius: 12,
					}}
				>
					<Title level={5} style={{ color: t.accent, marginBottom: 16 }}>
						Spacing & Controls
					</Title>

					<Row gutter={[24, 16]}>
						{[
							["Control SM", "24px"],
							["Control Default", "32px"],
							["Control LG", "40px"],
							["Border Radius", "8px"],
							["Max Content Width", "1800px"],
							["Base Font", "14px"],
						].map(([label, value]) => (
							<Col xs={12} sm={8} md={4} key={label}>
								<div
									style={{
										background: t.accentBg,
										borderRadius: 8,
										padding: "12px 16px",
										textAlign: "center",
										border: `1px solid ${t.accentBgActive}`,
									}}
								>
									<Text
										style={{
											color: t.textTertiary,
											fontSize: 10,
											textTransform: "uppercase",
											letterSpacing: 0.5,
											display: "block",
										}}
									>
										{label}
									</Text>
									<Text strong style={{ color: t.textPrimary, fontSize: 18 }}>
										{value}
									</Text>
								</div>
							</Col>
						))}
					</Row>
				</Card>
			</Col>
		</Row>
	</div>
);

// ─── Navigation ──────────────────────────────────────────────────────────────

const SECTIONS = [
	{ key: "foundation", label: "Foundation", icon: <BookOutlined /> },
	{ key: "personality", label: "Voice", icon: <SoundOutlined /> },
	{ key: "logos", label: "Logos", icon: <PictureOutlined /> },
	{ key: "brand-colors", label: "Brand Colors", icon: <BgColorsOutlined /> },
	{ key: "light-theme", label: "Light Theme", icon: <BgColorsOutlined /> },
	{ key: "dark-theme", label: "Dark Theme", icon: <BgColorsOutlined /> },
	{ key: "accent-colors", label: "Accents", icon: <BgColorsOutlined /> },
	{ key: "typography", label: "Typography", icon: <FontSizeOutlined /> },
	{ key: "shadows", label: "Shadows", icon: <LayoutOutlined /> },
];

// ─── Main Page ───────────────────────────────────────────────────────────────

const BrandBookPage = () => {
	const [activeSection, setActiveSection] = useState("foundation");
	const sectionRefs = useRef({});

	const t = PAGE_THEMES.dark;

	const scrollToSection = (key) => {
		setActiveSection(key);
		const el = sectionRefs.current[key];
		if (el) {
			el.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	const assignRef = (key) => (el) => {
		sectionRefs.current[key] = el;
	};

	return (
		<PageLayout
			fullWidthContent={
				<>
					{/* Hero */}
					<div
						style={{
							background: t.heroGradient,
							padding: "120px 24px 60px",
							textAlign: "center",
							borderBottom: `1px solid ${t.border}`,
							transition: "background 0.4s ease, border-color 0.3s ease",
						}}
					>
						<Logo.Favicon hyperlink={false} size={64} />
						<Title
							style={{
								marginTop: 16,
								marginBottom: 8,
								color: t.textPrimary,
								transition: "color 0.3s ease",
							}}
						>
							Brand Book
						</Title>
						<Paragraph
							style={{
								fontSize: 16,
								color: t.textTertiary,
								maxWidth: 600,
								margin: "0 auto 8px",
								transition: "color 0.3s ease",
							}}
						>
							{BRAND.description}
						</Paragraph>
						<Text
							style={{
								color: t.accentMuted,
								fontSize: 13,
								fontStyle: "italic",
							}}
						>
							{BRAND.tagline}
						</Text>
					</div>

					{/* Sticky nav */}
					<div
						style={{
							background: t.bgContainer,
							borderBottom: `1px solid ${t.border}`,
							position: "sticky",
							top: 0,
							zIndex: 100,
							overflowX: "auto",
							transition: "background 0.4s ease, border-color 0.3s ease",
						}}
					>
						<div
							style={{
								maxWidth: 1200,
								margin: "0 auto",
								display: "flex",
								alignItems: "center",
								gap: 8,
							}}
						>
							<Menu
								mode="horizontal"
								selectedKeys={[activeSection]}
								onClick={(e) => scrollToSection(e.key)}
								style={{
									background: "transparent",
									borderBottom: "none",
									flex: 1,
									justifyContent: "center",
								}}
								items={SECTIONS.map((s) => ({
									key: s.key,
									icon: s.icon,
									label: s.label,
								}))}
							/>
						</div>
					</div>

					{/* Content */}
					<div
						style={{
							background: t.contentGradient,
							minHeight: "100vh",
							transition: "background 0.4s ease",
						}}
					>
						<div
							style={{
								maxWidth: 1200,
								margin: "0 auto",
								padding: "48px 24px 96px",
							}}
						>
							<div ref={assignRef("foundation")}>
								<BrandFoundation t={t} />
							</div>
							<Divider
								style={{
									borderColor: t.border,
									margin: "48px 0",
									transition: "border-color 0.3s ease",
								}}
							/>

							<div ref={assignRef("personality")}>
								<PersonalityVoice t={t} />
							</div>
							<Divider
								style={{
									borderColor: t.border,
									margin: "48px 0",
									transition: "border-color 0.3s ease",
								}}
							/>

							<div ref={assignRef("logos")}>
								<LogoSection t={t} />
							</div>
							<Divider
								style={{
									borderColor: t.border,
									margin: "48px 0",
									transition: "border-color 0.3s ease",
								}}
							/>

							<div ref={assignRef("brand-colors")}>
								<BrandColorsSection t={t} />
							</div>
							<Divider
								style={{
									borderColor: t.border,
									margin: "48px 0",
									transition: "border-color 0.3s ease",
								}}
							/>

							<div ref={assignRef("light-theme")}>
								<LightThemeSection t={t} />
							</div>
							<Divider
								style={{
									borderColor: t.border,
									margin: "48px 0",
									transition: "border-color 0.3s ease",
								}}
							/>

							<div ref={assignRef("dark-theme")}>
								<DarkThemeSection t={t} />
							</div>
							<Divider
								style={{
									borderColor: t.border,
									margin: "48px 0",
									transition: "border-color 0.3s ease",
								}}
							/>

							<div ref={assignRef("accent-colors")}>
								<AccentColorsSection t={t} />
							</div>
							<Divider
								style={{
									borderColor: t.border,
									margin: "48px 0",
									transition: "border-color 0.3s ease",
								}}
							/>

							<div ref={assignRef("typography")}>
								<TypographySection t={t} />
							</div>
							<Divider
								style={{
									borderColor: t.border,
									margin: "48px 0",
									transition: "border-color 0.3s ease",
								}}
							/>

							<div ref={assignRef("shadows")}>
								<ShadowsSection t={t} />
							</div>

							{/* Footer tagline */}
							<Divider
								style={{
									borderColor: t.border,
									margin: "48px 0",
									transition: "border-color 0.3s ease",
								}}
							/>
							<div style={{ textAlign: "center", padding: "24px 0" }}>
								<Logo.Favicon hyperlink={false} size={32} />
								<Paragraph
									style={{
										color: t.accentMuted,
										marginTop: 12,
										fontStyle: "italic",
										fontSize: 15,
									}}
								>
									BodhiStreams — Tranquility, Harmony, Balance.
								</Paragraph>
							</div>
						</div>
					</div>
				</>
			}
		/>
	);
};

export default BrandBookPage;
